/**
 * UserDocsList — manages which docs a specific user has access to.
 *
 * Owner/Admin: read-only "Full access".
 * Regular members: list with editable roles + revoke + add.
 */
import {
  Button,
  Loading,
  Menu,
  MenuItem,
  MenuTrigger,
  notify,
  RowInput,
  Scrollable,
} from '@affine/component';
import { useDocMetaHelper } from '@affine/core/components/hooks/use-block-suite-page-meta';
import { WorkspaceServerService } from '@affine/core/modules/cloud';
import { DocMutationService } from '@affine/core/modules/doc';
import { objectRoleLabel } from '@affine/core/modules/permissions/utils/object-role-labels';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { DocRole, getUserDocGrantsQuery } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import {
  DeleteIcon,
  PageIcon,
  PlusIcon,
  SearchIcon,
} from '@blocksuite/icons/rc';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import * as styles from './styles.css';

interface UserDocsListProps {
  userId: string;
  userName: string;
  userPermission: string;
}

interface DocAccess {
  docId: string;
  role: string;
  title: string;
  isAreaBlocked?: boolean;
  areaBoundaryKind?: string | null;
  areaBoundaryRootFolderId?: string | null;
  areaBoundaryRootName?: string | null;
}

const GRANTABLE_DOC_ROLES = [
  DocRole.Manager,
  DocRole.Editor,
  DocRole.Commenter,
  DocRole.Reader,
];

const getAreaBlockedNotice = (
  t: ReturnType<typeof useI18n>,
  entry: DocAccess
) => {
  if (!entry.isAreaBlocked) {
    return null;
  }
  const areaName = entry.areaBoundaryRootName;
  if (entry.areaBoundaryKind === 'private' && areaName) {
    return t['com.streamlain.folder-share.area-blocked.private']({ areaName });
  }
  if (entry.areaBoundaryKind === 'teamspace' && areaName) {
    return t['com.streamlain.folder-share.area-blocked.teamspace']({
      areaName,
    });
  }
  return t['com.streamlain.folder-share.area-blocked.generic']();
};

export const UserDocsList = ({
  userId,
  userName: _userName,
  userPermission: _userPermission,
}: UserDocsListProps) => {
  const t = useI18n();
  const workspaceService = useService(WorkspaceService);
  const serverService = useService(WorkspaceServerService);
  const docMutationService = useService(DocMutationService);
  const docCollection = workspaceService.workspace.docCollection;
  const { getDocMeta } = useDocMetaHelper();
  const workspaceId = workspaceService.workspace.id;

  const [grants, setGrants] = useState<DocAccess[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  // Add doc state
  const [showAdd, setShowAdd] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  const [selectedRole, setSelectedRole] = useState<DocRole>(DocRole.Editor);

  const loadGrants = useCallback(async () => {
    if (!serverService.server) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const result = await serverService.server.gql({
        query: getUserDocGrantsQuery,
        variables: { workspaceId, userId },
      });
      setGrants(
        (result.workspace?.userDocGrants ?? []).map(g => ({
          docId: g.docId,
          role: g.role,
          title: getDocMeta(g.docId)?.title || 'Untitled',
          isAreaBlocked: g.isAreaBlocked,
          areaBoundaryKind: g.areaBoundaryKind,
          areaBoundaryRootFolderId: g.areaBoundaryRootFolderId,
          areaBoundaryRootName: g.areaBoundaryRootName,
        }))
      );
    } catch (err) {
      console.debug('Failed to load user doc grants:', err);
      setGrants([]);
    } finally {
      setIsLoading(false);
    }
  }, [serverService.server, workspaceId, userId, getDocMeta]);

  useEffect(() => {
    void loadGrants();
  }, [loadGrants]);

  const availableDocs = useMemo(() => {
    if (!showAdd) return [];
    const grantedIds = new Set(grants.map(g => g.docId));
    const docs: { id: string; title: string }[] = [];
    docCollection.docs.forEach((_doc, id) => {
      if (grantedIds.has(id)) return;
      const meta = getDocMeta(id);
      if (meta && !meta.trash) {
        docs.push({ id, title: meta.title || 'Untitled' });
      }
    });
    const q = addSearch.toLowerCase();
    return q ? docs.filter(d => d.title.toLowerCase().includes(q)) : docs;
  }, [showAdd, grants, docCollection.docs, getDocMeta, addSearch]);

  const handleGrant = useCallback(
    async (docId: string) => {
      setIsUpdating(true);
      try {
        await docMutationService.grantUserRoles({
          docId,
          userIds: [userId],
          role: selectedRole,
        });
        setGrants(prev => [
          ...prev.filter(grant => grant.docId !== docId),
          {
            docId,
            role: selectedRole,
            title: getDocMeta(docId)?.title || 'Untitled',
          },
        ]);
        notify.success({
          title:
            t['com.streamlain.settings.workspace.permissions.granted']?.() ??
            'Permission granted',
        });
        setShowAdd(false);
        setAddSearch('');
      } catch (err) {
        console.error('Failed to grant doc access:', err);
        notify.error({
          title:
            t[
              'com.streamlain.settings.workspace.permissions.grant-failed'
            ]?.() ?? 'Failed to grant permission',
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [docMutationService, getDocMeta, userId, selectedRole, t]
  );

  const handleUpdateRole = useCallback(
    async (docId: string, newRole: DocRole) => {
      setIsUpdating(true);
      try {
        await docMutationService.updateUserRole({
          docId,
          userId,
          role: newRole,
        });
        setGrants(prev =>
          prev.map(grant =>
            grant.docId === docId ? { ...grant, role: newRole } : grant
          )
        );
        notify.success({
          title:
            t['com.streamlain.settings.workspace.permissions.updated']?.() ??
            'Permission updated',
        });
      } catch (err) {
        console.error('Failed to update doc role:', err);
        notify.error({
          title:
            t[
              'com.streamlain.settings.workspace.permissions.update-failed'
            ]?.() ?? 'Failed to update permission',
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [docMutationService, userId, t]
  );

  const handleRevoke = useCallback(
    async (docId: string) => {
      setIsUpdating(true);
      try {
        await docMutationService.revokeUserRole({
          docId,
          userId,
        });
        setGrants(prev => prev.filter(grant => grant.docId !== docId));
        notify.success({
          title:
            t['com.streamlain.settings.workspace.permissions.revoked']?.() ??
            'Access removed',
        });
      } catch (err) {
        console.error('Failed to revoke doc role:', err);
        notify.error({
          title:
            t[
              'com.streamlain.settings.workspace.permissions.revoke-failed'
            ]?.() ?? 'Failed to remove access',
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [docMutationService, userId, t]
  );

  const getRoleLabel = useCallback(
    (role: string) => objectRoleLabel(t, role),
    [t]
  );

  if (isLoading) {
    return (
      <div className={styles.loadingState}>
        <Loading size={16} />
      </div>
    );
  }

  return (
    <div className={styles.editorContainer}>
      <div className={styles.editorHeader}>
        <span className={styles.editorLabel}>
          {t[
            'com.streamlain.settings.workspace.permissions.user-doc-access'
          ]?.() ?? 'Document access'}
        </span>
        <Button
          variant="plain"
          prefix={<PlusIcon />}
          onClick={() => setShowAdd(!showAdd)}
          disabled={isUpdating}
        >
          {t['com.streamlain.settings.workspace.permissions.add-user']?.() ??
            'Add'}
        </Button>
      </div>

      {showAdd && (
        <>
          <div className={styles.inviteContainer}>
            <div className={styles.inviteSearch}>
              <SearchIcon className={styles.searchIcon} />
              <RowInput
                value={addSearch}
                onChange={setAddSearch}
                placeholder={
                  t[
                    'com.streamlain.settings.workspace.permissions.search-docs'
                  ]?.() ?? 'Search documents...'
                }
                className={styles.searchInputField}
              />
            </div>
            <Menu
              items={GRANTABLE_DOC_ROLES.map(role => (
                <MenuItem
                  key={role}
                  selected={role === selectedRole}
                  onSelect={() => setSelectedRole(role)}
                >
                  {getRoleLabel(role)}
                </MenuItem>
              ))}
              contentOptions={{ align: 'end' }}
            >
              <MenuTrigger className={styles.ownerMenuTrigger}>
                {getRoleLabel(selectedRole)}
              </MenuTrigger>
            </Menu>
          </div>

          {availableDocs.length > 0 && (
            <Scrollable.Root className={styles.inviteMembersList}>
              <Scrollable.Viewport>
                {availableDocs.slice(0, 20).map(doc => (
                  <div
                    key={doc.id}
                    className={styles.inviteMemberRow}
                    onClick={() => {
                      void handleGrant(doc.id);
                    }}
                  >
                    <PageIcon
                      style={{ width: 20, height: 20, flexShrink: 0 }}
                    />
                    <div className={styles.listItemContent}>
                      <div className={styles.listItemTitle}>{doc.title}</div>
                    </div>
                  </div>
                ))}
              </Scrollable.Viewport>
              <Scrollable.Scrollbar />
            </Scrollable.Root>
          )}

          {availableDocs.length === 0 && addSearch && (
            <div className={styles.noMembersFound}>
              {t[
                'com.streamlain.settings.workspace.permissions.no-docs-found'
              ]?.() ?? 'No documents found'}
            </div>
          )}
        </>
      )}

      {grants.length === 0 ? (
        <div className={styles.emptyPermissions}>
          {t[
            'com.streamlain.settings.workspace.permissions.user-no-docs'
          ]?.() ?? 'No specific document permissions.'}
        </div>
      ) : (
        <div className={styles.grantedUsersList}>
          {grants.map(grant => {
            const areaBlockedNotice = getAreaBlockedNotice(t, grant);

            return (
              <div
                key={grant.docId}
                className={styles.permissionRowWrapper}
                data-area-blocked={grant.isAreaBlocked ? 'true' : undefined}
              >
                <div
                  className={`${styles.permissionRow} ${
                    grant.isAreaBlocked ? styles.areaBlockedPermissionRow : ''
                  }`}
                >
                  <div className={styles.permissionUser}>
                    <PageIcon
                      style={{ width: 16, height: 16, flexShrink: 0 }}
                    />
                    <div className={styles.listItemContent}>
                      <div className={styles.listItemTitle}>{grant.title}</div>
                    </div>
                  </div>

                  <Menu
                    items={GRANTABLE_DOC_ROLES.map(role => (
                      <MenuItem
                        key={role}
                        selected={role === grant.role}
                        onSelect={() => {
                          void handleUpdateRole(grant.docId, role);
                        }}
                      >
                        {getRoleLabel(role)}
                      </MenuItem>
                    ))}
                    contentOptions={{ align: 'end' }}
                  >
                    <MenuTrigger
                      className={styles.ownerMenuTrigger}
                      disabled={isUpdating}
                    >
                      {getRoleLabel(grant.role)}
                    </MenuTrigger>
                  </Menu>

                  <Button
                    variant="plain"
                    className={styles.removeButton}
                    onClick={() => {
                      void handleRevoke(grant.docId);
                    }}
                    disabled={isUpdating}
                  >
                    <DeleteIcon />
                  </Button>
                </div>
                {areaBlockedNotice ? (
                  <div className={styles.areaBlockedNotice}>
                    {areaBlockedNotice}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
